/**
 * bundle-offer.js
 * "Buy 1 for ₹999, Get 2 FREE" Bundle Promotion
 * Theme: Canyon (Shah Collection)
 *
 * Uses existing Shopify AJAX Cart API — integrates with your existing cart drawer.
 * No custom cart system. No overrides.
 */

(function () {
  'use strict';

  class BundleOffer {
    constructor(section) {
      this.section = section;
      this.paidPrice = parseInt(section.dataset.paidPrice, 10); // in paise (₹999 = 99900)

      // State
      this.selectedPaid = null;   // { id, variantId, title, image }
      this.selectedFree = [];     // Array of up to 2 products

      // All products from JSON in DOM
      this.allProducts = this._parseProducts();

      // DOM refs
      this.paidGrid   = section.querySelector('.js-paid-grid');
      this.freeGrid   = section.querySelector('.js-free-grid');
      this.stepFree   = section.querySelector('.js-step-free');
      this.summary    = section.querySelector('.js-bundle-summary');
      this.summaryItems = section.querySelector('.js-summary-items');
      this.addBtn     = section.querySelector('.js-add-bundle-btn');
      this.btnText    = section.querySelector('.js-btn-text');
      this.btnSpinner = section.querySelector('.bundle-offer__btn-spinner');
      this.error      = section.querySelector('.js-bundle-error');
      this.paidCount  = section.querySelector('.js-paid-count');
      this.freeCount  = section.querySelector('.js-free-count');

      this._bindEvents();
    }

    // ─── PARSE PRODUCTS JSON ───────────────────────────────────────────
    _parseProducts() {
      const el = document.getElementById('bundle-products-data');
      if (!el) return [];
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[BundleOffer] Could not parse product data:', e);
        return [];
      }
    }

    // ─── BIND EVENTS ──────────────────────────────────────────────────
    _bindEvents() {
      // Paid product selection (event delegation on paid grid)
      this.paidGrid.addEventListener('click', (e) => {
        const card = e.target.closest('.js-paid-card');
        if (!card) return;
        this._selectPaid(card);
      });

      // Add bundle to cart
      this.addBtn.addEventListener('click', () => {
        if (!this.addBtn.disabled) this._addBundleToCart();
      });
    }

    // ─── SELECT PAID PRODUCT ──────────────────────────────────────────
    _selectPaid(card) {
      // Deselect previous
      this.paidGrid.querySelectorAll('.js-paid-card').forEach(c => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-pressed', 'false');
      });

      // Select new
      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');

      this.selectedPaid = {
        id:        parseInt(card.dataset.productId, 10),
        variantId: parseInt(card.dataset.variantId, 10),
        title:     card.dataset.productTitle,
        image:     card.dataset.productImage,
      };

      // Reset free selections
      this.selectedFree = [];

      // Update counter
      this._updateCounters();

      // Build free product grid (excluding selected paid product)
      this._buildFreeGrid();

      // Show step 2
      this.stepFree.style.display = '';

      // Scroll to step 2 smoothly
      setTimeout(() => {
        this.stepFree.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      // Update UI
      this._updateSummary();
      this._updateButton();
    }

    // ─── BUILD FREE PRODUCTS GRID ─────────────────────────────────────
    _buildFreeGrid() {
      this.freeGrid.innerHTML = '';

      const freeProducts = this.allProducts.filter(p => p.id !== this.selectedPaid.id);

      freeProducts.forEach(product => {
        const card = document.createElement('div');
        card.className = 'bundle-offer__card js-bundle-card js-free-card';
        card.dataset.productId  = product.id;
        card.dataset.variantId  = product.variantId;
        card.dataset.productTitle = product.title;
        card.dataset.productImage = product.image;
        card.dataset.type = 'free';
        card.setAttribute('role', 'button');
        card.setAttribute('tabindex', '0');
        card.setAttribute('aria-pressed', 'false');

        card.innerHTML = `
          <div class="bundle-offer__card-image-wrap">
            ${product.image
              ? `<img src="${product.image}" alt="${this._esc(product.imageAlt)}" class="bundle-offer__card-image" loading="lazy" width="400">`
              : `<div class="bundle-offer__card-placeholder"></div>`
            }
            <div class="bundle-offer__card-badge bundle-offer__card-badge--free">FREE</div>
            <div class="bundle-offer__card-selected-mark">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="currentColor"/>
                <path d="M7 12.5L10.5 16L17 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
          <div class="bundle-offer__card-info">
            <p class="bundle-offer__card-title">${this._esc(product.title)}</p>
            <div class="bundle-offer__card-prices">
              <span class="bundle-offer__card-price-new">FREE</span>
              <span class="bundle-offer__card-price-old">₹${(product.price / 100).toFixed(0)}</span>
            </div>
          </div>
        `;

        // Click handler
        card.addEventListener('click', () => this._toggleFree(card, product));
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._toggleFree(card, product);
          }
        });

        this.freeGrid.appendChild(card);
      });
    }

    // ─── TOGGLE FREE PRODUCT ──────────────────────────────────────────
    _toggleFree(card, product) {
      const alreadySelected = this.selectedFree.findIndex(p => p.id === product.id);

      if (alreadySelected > -1) {
        // Deselect
        this.selectedFree.splice(alreadySelected, 1);
        card.classList.remove('is-selected');
        card.setAttribute('aria-pressed', 'false');
      } else {
        // Select (max 2)
        if (this.selectedFree.length >= 2) {
          this._showError('You can only select 2 FREE products. Please deselect one first.');
          return;
        }
        this._hideError();
        this.selectedFree.push({
          id:        product.id,
          variantId: product.variantId,
          title:     product.title,
          image:     product.image,
        });
        card.classList.add('is-selected');
        card.setAttribute('aria-pressed', 'true');
      }

      this._updateCounters();
      this._updateSummary();
      this._updateButton();
    }

    // ─── UPDATE COUNTERS ──────────────────────────────────────────────
    _updateCounters() {
      this.paidCount.textContent = this.selectedPaid ? 1 : 0;
      this.freeCount.textContent = this.selectedFree.length;
    }

    // ─── UPDATE SUMMARY ───────────────────────────────────────────────
    _updateSummary() {
      if (!this.selectedPaid) {
        this.summary.style.display = 'none';
        return;
      }

      this.summary.style.display = '';

      let html = '';

      // Paid product
      html += this._summaryRow(this.selectedPaid, `₹${this.paidPrice / 100}`, 'Paid');

      // Free products
      this.selectedFree.forEach(p => {
        html += this._summaryRow(p, 'FREE', 'Free');
      });

      // Placeholder rows for remaining free slots
      const remaining = 2 - this.selectedFree.length;
      for (let i = 0; i < remaining; i++) {
        html += `
          <div class="bundle-offer__summary-row bundle-offer__summary-row--empty">
            <div class="bundle-offer__summary-img-wrap">
              <div class="bundle-offer__summary-img-placeholder"></div>
            </div>
            <div class="bundle-offer__summary-row-info">
              <span class="bundle-offer__summary-row-title">Choose a FREE product</span>
            </div>
            <span class="bundle-offer__summary-row-price bundle-offer__summary-row-price--free">FREE</span>
          </div>
        `;
      }

      this.summaryItems.innerHTML = html;
    }

    _summaryRow(product, priceLabel, typeLabel) {
      const isFree = typeLabel === 'Free';
      return `
        <div class="bundle-offer__summary-row">
          <div class="bundle-offer__summary-img-wrap">
            ${product.image
              ? `<img src="${product.image}" alt="${this._esc(product.title)}" class="bundle-offer__summary-img" loading="lazy">`
              : `<div class="bundle-offer__summary-img-placeholder"></div>`
            }
          </div>
          <div class="bundle-offer__summary-row-info">
            <span class="bundle-offer__summary-row-label ${isFree ? 'bundle-offer__summary-row-label--free' : ''}">${typeLabel}</span>
            <span class="bundle-offer__summary-row-title">${this._esc(product.title)}</span>
          </div>
          <span class="bundle-offer__summary-row-price ${isFree ? 'bundle-offer__summary-row-price--free' : ''}">${priceLabel}</span>
        </div>
      `;
    }

    // ─── UPDATE BUTTON STATE ──────────────────────────────────────────
    _updateButton() {
      const ready = this.selectedPaid && this.selectedFree.length === 2;
      this.addBtn.disabled = !ready;
    }

    // ─── ADD BUNDLE TO CART ───────────────────────────────────────────
    async _addBundleToCart() {
      this._setLoading(true);
      this._hideError();

      try {
        const items = [
          // Paid product at fixed price
          {
            id: this.selectedPaid.variantId,
            quantity: 1,
            properties: {
              '_bundle_role': 'paid',
              '_bundle_offer': 'buy1get2free'
            }
          },
          // Free products at price 0 (using Shopify discount or price override via line item)
          ...this.selectedFree.map(p => ({
            id: p.variantId,
            quantity: 1,
            properties: {
              '_bundle_role': 'free',
              '_bundle_offer': 'buy1get2free',
              'Free Gift': '✓'
            }
          }))
        ];

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Could not add items to cart. Please try again.');
        }

        // ── Trigger your existing Canyon cart drawer ──
        // Canyon theme dispatches/listens to these standard events:
        this._triggerCartOpen();

        // Reset selections after success
        this._resetSelections();

      } catch (err) {
        this._showError(err.message || 'Something went wrong. Please try again.');
      } finally {
        this._setLoading(false);
      }
    }

    // ─── TRIGGER EXISTING CART DRAWER ─────────────────────────────────
    _triggerCartOpen() {
      // Canyon / Dawn-compatible cart open events — tries all known methods.
      // Your existing drawer will respond to whichever one it uses.

      // Method 1: Custom event (Dawn & Canyon default)
      document.dispatchEvent(new CustomEvent('cart:open'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Method 2: Canyon specific open event
      document.dispatchEvent(new CustomEvent('CartDrawer:open'));

      // Method 3: Global function fallback (some Canyon builds)
      if (typeof window.openCartDrawer === 'function') {
        window.openCartDrawer();
      }

      // Method 4: Trigger click on hidden cart icon (ultimate fallback)
      const cartBtn = document.querySelector('[data-cart-icon-bubble], .js-cart-icon, #cart-icon-bubble, [href="/cart"]');
      if (cartBtn && !cartBtn.dataset.bundleTriggered) {
        cartBtn.dataset.bundleTriggered = 'true';
        cartBtn.click();
        setTimeout(() => delete cartBtn.dataset.bundleTriggered, 500);
      }
    }

    // ─── RESET AFTER ADDING ───────────────────────────────────────────
    _resetSelections() {
      this.selectedPaid = null;
      this.selectedFree = [];

      // Deselect all paid cards
      this.paidGrid.querySelectorAll('.js-paid-card').forEach(c => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-pressed', 'false');
      });

      // Hide step 2 and summary
      this.stepFree.style.display = 'none';
      this.summary.style.display = 'none';

      // Reset counters and button
      this._updateCounters();
      this._updateButton();

      // Scroll back to top of section
      this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // ─── UI HELPERS ───────────────────────────────────────────────────
    _setLoading(loading) {
      this.addBtn.disabled = loading;
      this.btnText.style.display  = loading ? 'none' : '';
      this.btnSpinner.style.display = loading ? '' : 'none';
    }

    _showError(msg) {
      this.error.textContent = msg;
      this.error.style.display = '';
      this.error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    _hideError() {
      this.error.textContent = '';
      this.error.style.display = 'none';
    }

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  // ─── INIT ──────────────────────────────────────────────────────────
  function init() {
    const section = document.querySelector('[data-section-id].bundle-offer-section');
    if (section) new BundleOffer(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
